import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Lock, Smartphone, Globe, HelpCircle, History, Mail } from "lucide-react";
import { useSEO } from "../lib/seo";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-light)] p-5">
      <h2 className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[1.5px] text-gold">
        <Icon size={14} /> {title}
      </h2>
      <div className="mt-3 text-sm text-text leading-relaxed">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />
      <span>{children}</span>
    </li>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  useSEO({ title: "Privacy Policy", description: "GurSetu Privacy Policy", path: "/privacy" });

  return (
    <div className="min-h-screen bg-app">
      <header className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-[var(--glass-border)] glass">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full grid place-items-center btn-ghost" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-heading text-lg font-extrabold text-text">Privacy Policy</h1>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 space-y-4 pb-28">
        <Section icon={HeartHandshake} title="Our Promise">
          <p>Your privacy is sacred to us. GurSetu is built on the principle of complete transparency — we collect nothing, store nothing on servers, and sell nothing. Ever.</p>
        </Section>

        <Section icon={Lock} title="Data We Collect">
          <p className="mb-2">None. Zero. Nada.</p>
          <ul className="space-y-1.5">
            <Bullet>No personal information</Bullet>
            <Bullet>No email addresses</Bullet>
            <Bullet>No phone numbers</Bullet>
            <Bullet>No location tracking</Bullet>
            <Bullet>No analytics or cookies</Bullet>
          </ul>
        </Section>

        <Section icon={Smartphone} title="Local Storage">
          <p className="mb-2">All your data stays on your device only:</p>
          <ul className="space-y-1.5">
            <Bullet>Saved Hukamnamas</Bullet>
            <Bullet>Personal reflections</Bullet>
            <Bullet>App preferences</Bullet>
            <Bullet>Simran progress</Bullet>
          </ul>
          <p className="mt-3">This data never leaves your phone. If you uninstall the app, it's gone forever.</p>
        </Section>

        <Section icon={Globe} title="Third-Party Services">
          <p className="mb-2">We use BaniDB API to fetch Gurbani content. This API:</p>
          <ul className="space-y-1.5">
            <Bullet>Does not track users</Bullet>
            <Bullet>Is open source</Bullet>
            <Bullet>Requires no authentication</Bullet>
          </ul>
          <p className="mt-3">We do not use Google Analytics, Facebook Pixel, or any tracking SDKs.</p>
        </Section>

        <Section icon={HelpCircle} title="Children's Privacy">
          <p>GurSetu is designed for all ages. We do not knowingly collect any information from children under 13. Our Kids Mode is fully offline and private.</p>
        </Section>

        <Section icon={History} title="Changes to This Policy">
          <p>We may update this policy occasionally. Any changes will be reflected in-app with a clear notification. We will never make changes that compromise your privacy.</p>
        </Section>

        <Section icon={Mail} title="Contact Us">
          <p className="mb-1">Questions about privacy? Reach out to us:</p>
          <a href="mailto:support@gursetu.app" className="inline-flex items-center gap-2 font-semibold text-gold">
            📧 support@gursetu.app
          </a>
        </Section>

        <p className="pt-2 text-center text-xs italic text-gold">May Waheguru bless your journey. 🙏</p>
      </div>
    </div>
  );
}